import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import { getProfitAnalyticsLines } from 'client/utils/api-routes-analytics'
import {
  colors,
  DcBreadcrumbs,
  DcButton,
  DcDiv,
  DcFlexBox,
  PixelPageContainer,
  PixelText,
  Section,
  SectionContent,
} from 'pixel-base'
import styled from 'styled-components'

import { ProfitLine } from './types'
import {
  formatPKR,
  formatReportDate,
  getLinePurchasePrice,
  getLineSalePrice,
  getLineTotalCost,
  getLineTotalSale,
} from './utils'

const ProfitAnalyticsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const initialDate = searchParams.get('d') || ''
  const initialCount = parseInt(searchParams.get('c') || '0', 10)
  const initialQty = parseInt(searchParams.get('q') || '0', 10)
  const initialTotalSale = parseFloat(searchParams.get('ts') || '0')
  const initialTotalCost = parseFloat(searchParams.get('tc') || '0')
  const initialDiscount = parseFloat(searchParams.get('ds') || '0')
  const initialProfit = parseFloat(searchParams.get('p') || '0')

  const [lines, setLines] = useState<ProfitLine[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLines = useCallback(() => {
    if (!id) return

    setIsLoading(true)
    setError(null)

    getProfitAnalyticsLines(id).subscribe({
      next: (res: any) => {
        const result = res?.response
        let linesData: ProfitLine[] = []

        if (Array.isArray(result)) {
          linesData = result
        } else if (result && typeof result === 'object') {
          if (result.error) {
            setError(result.error)
            setIsLoading(false)
            return
          }
          if (Array.isArray(result.data)) {
            linesData = result.data
          }
        }

        setLines(linesData)
        setIsLoading(false)
      },
      error: (err: any) => {
        const status = err?.status
        setError(
          status
            ? `Failed to fetch profit details (Status: ${status})`
            : 'Failed to fetch profit details. Please try again.',
        )
        setIsLoading(false)
      },
    })
  }, [id])

  useEffect(() => {
    fetchLines()
  }, [fetchLines])

  const displayDate =
    isLoading && initialDate
      ? formatReportDate(initialDate)
      : lines.length > 0
      ? formatReportDate(lines[0].report_date)
      : formatReportDate(initialDate)

  const hasLines = lines.length > 0
  const summary = {
    totalQty: hasLines
      ? lines.reduce((sum, l) => sum + (l.qty_sold || 0), 0)
      : initialQty,
    productCount: hasLines ? lines.length : initialCount,
    totalSale: hasLines
      ? lines.reduce((sum, l) => sum + getLineTotalSale(l), 0)
      : initialTotalSale,
    totalCost: hasLines
      ? lines.reduce((sum, l) => sum + getLineTotalCost(l), 0)
      : initialTotalCost,
    totalDiscount: hasLines
      ? lines.reduce((sum, l) => sum + (l.discount_amount || 0), 0)
      : initialDiscount,
    totalProfit: hasLines
      ? lines.reduce((sum, l) => sum + (l.simple_profit || 0), 0)
      : initialProfit,
  }

  const breadcrumbs = [
    { name: 'Analytics', active: false },
    { name: 'Profit Analytics Engine', active: false },
    { name: `Report #${id}`, active: true },
  ]

  return (
    <DcFlexBox flexDirection="column">
      <DcFlexBox
        padding="0px 20px"
        height="60px"
        style={{
          justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
        }}
        alignItems="center"
      >
        <DcFlexBox alignItems="center" width="auto">
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
        </DcFlexBox>
        <DcDiv>
          <ProfileSection />
        </DcDiv>
      </DcFlexBox>

      <PixelPageContainer padding="0px">
        <Section style={{ padding: '20px' }}>
          <SectionContent style={{ padding: '20px', borderRadius: '8px' }}>
            <HeaderRow>
              <TitleBlock>
                <DcButton
                  variant="outline-secondary"
                  onClick={() => navigate('/analytics/profit-analytics')}
                >
                  ← Back
                </DcButton>
                <div>
                  <PageTitle>
                    Profit Report Detail <Badge>#{id}</Badge>
                  </PageTitle>
                  <PageSubtitle>
                    {displayDate || 'Daily breakdown'}
                  </PageSubtitle>
                </div>
              </TitleBlock>
              <DcButton
                variant="outline-secondary"
                onClick={fetchLines}
                disabled={isLoading}
              >
                {isLoading ? 'Refreshing...' : 'Refresh'}
              </DcButton>
            </HeaderRow>

            <SummaryGrid>
              <SummaryCard $tone="blue">
                <SummaryLabel>Total Qty</SummaryLabel>
                <SummaryValue>{summary.totalQty}</SummaryValue>
              </SummaryCard>
              <SummaryCard $tone="purple">
                <SummaryLabel>Products</SummaryLabel>
                <SummaryValue>{summary.productCount}</SummaryValue>
              </SummaryCard>
              <SummaryCard $tone="emerald">
                <SummaryLabel>Total Sale</SummaryLabel>
                <SummaryValue>{formatPKR(summary.totalSale)}</SummaryValue>
              </SummaryCard>
              <SummaryCard $tone="orange">
                <SummaryLabel>Total Cost</SummaryLabel>
                <SummaryValue>{formatPKR(summary.totalCost)}</SummaryValue>
              </SummaryCard>
              <SummaryCard $tone="red">
                <SummaryLabel>Discounts</SummaryLabel>
                <SummaryValue>{formatPKR(summary.totalDiscount)}</SummaryValue>
              </SummaryCard>
              <SummaryCard $tone="green">
                <SummaryLabel>Net Profit</SummaryLabel>
                <SummaryValue>{formatPKR(summary.totalProfit)}</SummaryValue>
              </SummaryCard>
            </SummaryGrid>

            <TableCard>
              <TableHeader>
                <PixelText fontSize="15px" fontWeight="600">
                  Product Breakdown
                </PixelText>
              </TableHeader>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th>Product Information</th>
                      <th className="center">Qty Sold</th>
                      <th>Sale Price</th>
                      <th>Purchase Price</th>
                      <th>Total Sale</th>
                      <th>Total Cost</th>
                      <th>Discount</th>
                      <th>Net Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                          <td>
                            <ProductCell>
                              <ImageSkeleton />
                              <div>
                                <Skeleton $width="140px" />
                                <Skeleton $width="80px" $mt />
                              </div>
                            </ProductCell>
                          </td>
                          {Array.from({ length: 7 }).map((__, j) => (
                            <td key={j}>
                              <Skeleton />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : error ? (
                      <tr>
                        <td colSpan={8}>
                          <EmptyState>
                            <PixelText
                              style={{ color: '#dc2626', marginBottom: 8 }}
                            >
                              {error}
                            </PixelText>
                            <DcButton
                              variant="outline-secondary"
                              onClick={fetchLines}
                            >
                              Try again
                            </DcButton>
                          </EmptyState>
                        </td>
                      </tr>
                    ) : lines.length === 0 ? (
                      <tr>
                        <td colSpan={8}>
                          <EmptyState>
                            No product data found for this report
                          </EmptyState>
                        </td>
                      </tr>
                    ) : (
                      lines.map((line) => (
                        <tr key={line.id}>
                          <td>
                            <ProductCell>
                              <ProductImage>
                                {line.product_image ? (
                                  <img
                                    src={line.product_image}
                                    alt={line.product_name}
                                    onError={(e) => {
                                      ;(
                                        e.target as HTMLImageElement
                                      ).style.display = 'none'
                                    }}
                                  />
                                ) : (
                                  <NoImg>No Img</NoImg>
                                )}
                              </ProductImage>
                              <div>
                                <ProductName>{line.product_name}</ProductName>
                                <SkuRow>
                                  <SkuBadge>SKU</SkuBadge>
                                  {line.product_sku}
                                </SkuRow>
                              </div>
                            </ProductCell>
                          </td>
                          <td className="center">
                            <Pill>{line.qty_sold}</Pill>
                          </td>
                          <td>
                            <Muted>{formatPKR(getLineSalePrice(line))}</Muted>
                          </td>
                          <td>
                            <Muted>
                              {formatPKR(getLinePurchasePrice(line))}
                            </Muted>
                          </td>
                          <td>
                            <Sale>{formatPKR(getLineTotalSale(line))}</Sale>
                          </td>
                          <td>
                            <Muted>{formatPKR(getLineTotalCost(line))}</Muted>
                          </td>
                          <td>
                            {line.discount_amount ? (
                              <DiscountPill>
                                -{formatPKR(Math.abs(line.discount_amount))}
                              </DiscountPill>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <Profit $positive={line.simple_profit >= 0}>
                              {formatPKR(line.simple_profit)}
                            </Profit>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </TableScroll>
            </TableCard>
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </DcFlexBox>
  )
}

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const PageTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const Badge = styled.span`
  font-size: 12px;
  font-family: monospace;
  font-weight: 600;
  padding: 2px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #4b5563;
`

const PageSubtitle = styled.p`
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 13px;
`

const SummaryGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  margin-bottom: 20px;
`

const SummaryCard = styled.div<{
  $tone: 'blue' | 'purple' | 'emerald' | 'orange' | 'red' | 'green'
}>`
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-left: 3px solid
    ${(props) =>
      props.$tone === 'blue'
        ? '#3b82f6'
        : props.$tone === 'purple'
        ? '#8b5cf6'
        : props.$tone === 'emerald'
        ? '#10b981'
        : props.$tone === 'orange'
        ? '#f97316'
        : props.$tone === 'red'
        ? '#ef4444'
        : '#22c55e'};
`

const SummaryLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
`

const SummaryValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  font-variant-numeric: tabular-nums;
`

const TableCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`

const TableHeader = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
`

const TableScroll = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;

  th {
    padding: 12px 16px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6b7280;
    text-align: left;
    background: #fafafa;
    border-bottom: 1px solid #e5e7eb;

    &.center {
      text-align: center;
    }

    &:first-child {
      padding-left: 20px;
    }

    &:last-child {
      padding-right: 20px;
    }
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;

    &.center {
      text-align: center;
    }

    &:first-child {
      padding-left: 20px;
    }

    &:last-child {
      padding-right: 20px;
    }
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`

const ProductCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ProductImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const NoImg = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  text-transform: uppercase;
  color: #9ca3af;
`

const ProductName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SkuRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 12px;
  font-family: monospace;
  color: #6b7280;
`

const SkuBadge = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  background: #f3f4f6;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
`

const Pill = styled.span`
  display: inline-flex;
  min-width: 74px;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  background: #f5f3ff;
  color: #6d28d9;
`

const Muted = styled.span`
  color: #6b7280;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`

const Sale = styled.span`
  color: #047857;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`

const DiscountPill = styled.span`
  display: inline-flex;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #fef2f2;
  color: #dc2626;
  font-variant-numeric: tabular-nums;
`

const Profit = styled.span<{ $positive: boolean }>`
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(props) => (props.$positive ? '#16a34a' : '#dc2626')};
`

const EmptyState = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: #6b7280;
`

const Skeleton = styled.div<{ $width?: string; $mt?: boolean }>`
  height: 14px;
  width: ${(props) => props.$width || '70%'};
  margin-top: ${(props) => (props.$mt ? '8px' : '0')};
  border-radius: 4px;
  background: #e5e7eb;
  animation: pulse 1.2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.45;
    }
  }
`

const ImageSkeleton = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: #e5e7eb;
  flex-shrink: 0;
`

export default ProfitAnalyticsDetail
